// src/pages/CustomerForm.jsx

import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from '../../axios';
import { 
  Container, Paper, Typography, Grid, TextField, Button, Box, Switch, 
  FormControlLabel, CircularProgress, Dialog, DialogContent, InputLabel, Select, MenuItem, FormControl
} from "@mui/material";
import { PersonAdd as PersonAddIcon } from "@mui/icons-material";

export default function CustomerForm({ onSuccess, onCancel, isModal = false }) {
  const navigate = useNavigate();
  const { id } = useParams();
  const editingId = id ? parseInt(id, 10) : null;

  const companyId = parseInt(localStorage.getItem('companyId'), 10) || 0;
  const branchId = parseInt(localStorage.getItem('branchId'), 10) || 0;

  const [form, setForm] = useState({
    customerType: 1,
    name: '',
    taxNumber: '',
    mobile: '',
    phone: '',
    email: '',
    address: '',
    buildingNumber: '',
    additionalNumber: '',
    unitNumber: '',
    streetName: '',
    district: '',
    city: '',
    country: '',
    postalCode: '',
    shortAddress: '',
    notes: '',
    isActive: true
  });

  const [loading, setLoading] = useState(false);
  const [successModal, setSuccessModal] = useState(false);

  useEffect(() => {
    if (!editingId) return;
    axios.get(`/Customer/${editingId}`)
      .then(res => {
        const d = res.data;
        setForm({
          customerType: d.customerType || 1,
          name: d.name || '',
          taxNumber: d.taxNumber || '',
          mobile: d.mobile || '',
          phone: d.phone || '',
          email: d.email || '',
          address: d.address || '',
          buildingNumber: d.buildingNumber || '',
          additionalNumber: d.additionalNumber || '',
          unitNumber: d.unitNumber || '',
          streetName: d.streetName || '',
          district: d.district || '',
          city: d.city || '',
          country: d.country || '',
          postalCode: d.postalCode || '',
          shortAddress: d.shortAddress || '',
          notes: d.notes || '',
          isActive: d.isActive
        });
      })
      .catch(() => alert('❌ تعذّر تحميل بيانات العميل'));
  }, [editingId]);

  const handleChange = e => {
    const { name, value, type, checked } = e.target;
    setForm(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async e => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = { ...form, companyId, branchId };
      let response;
      if (editingId) {
        response = await axios.put(`/Customer/${editingId}`, payload);
      } else {
        response = await axios.post(`/Customer`, payload);
      }

      setSuccessModal(true);
      setTimeout(() => {
        setSuccessModal(false);
        if (onSuccess) {
          onSuccess({ ...form, id: response.data.id || response.data.Id, ...response.data });
        } else {
          navigate('/customers');
        }
      }, 2000);
    } catch (err) {
      const data = err.response?.data;
      alert(data?.title || '❌ حدث خطأ أثناء الحفظ');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="md" sx={{ mt: 4, mb: 6 }} dir="rtl">
      <Paper elevation={0} sx={{ p: 4, borderRadius: '24px', border: '1px solid #e2e8f0' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 4 }}>
          <PersonAddIcon sx={{ fontSize: 40, color: '#1e40af' }} />
          <Typography variant="h4" fontWeight="800">
            {editingId ? 'تعديل بيانات العميل' : 'إضافة عميل جديد'}
          </Typography>
        </Box>

        <form onSubmit={handleSubmit} className="space-y-6">
          <FormControl fullWidth>
            <InputLabel>نوع العميل</InputLabel>
            <Select name="customerType" value={form.customerType} label="نوع العميل" onChange={handleChange}>
              <MenuItem value={1}>فردي</MenuItem>
              <MenuItem value={2}>تجاري</MenuItem>
            </Select>
          </FormControl>

          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TextField fullWidth label="الاسم" name="name" value={form.name} onChange={handleChange} required sx={{ '& .MuiOutlinedInput-root': { borderRadius: '16px' } }} />
            </Grid>
            {parseInt(form.customerType) === 2 && (
              <Grid item xs={12} md={6}>
                <TextField fullWidth label="الرقم الضريبي" name="taxNumber" value={form.taxNumber} onChange={handleChange} required sx={{ '& .MuiOutlinedInput-root': { borderRadius: '16px' } }} />
              </Grid>
            )}
            <Grid item xs={12} md={6}>
              <TextField fullWidth label="الجوال" name="mobile" value={form.mobile} onChange={handleChange} sx={{ '& .MuiOutlinedInput-root': { borderRadius: '16px' } }} />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField fullWidth label="الهاتف" name="phone" value={form.phone} onChange={handleChange} sx={{ '& .MuiOutlinedInput-root': { borderRadius: '16px' } }} />
            </Grid>
          </Grid>

          {/* العنوان الوطني */}
          <Typography variant="h6" sx={{ mt: 2, color: '#1e40af' }}>العنوان الوطني</Typography>
          <Grid container spacing={2}>
            <Grid item xs={6} md={3}><TextField fullWidth label="رقم المبنى" name="buildingNumber" value={form.buildingNumber} onChange={handleChange} /></Grid>
            <Grid item xs={6} md={3}><TextField fullWidth label="اسم الشارع" name="streetName" value={form.streetName} onChange={handleChange} /></Grid>
            <Grid item xs={6} md={3}><TextField fullWidth label="الحي" name="district" value={form.district} onChange={handleChange} /></Grid>
            <Grid item xs={6} md={3}><TextField fullWidth label="المدينة" name="city" value={form.city} onChange={handleChange} /></Grid>
          </Grid>

          <FormControlLabel control={<Switch name="isActive" checked={form.isActive} onChange={handleChange} />} label="نشط" />

          <Button type="submit" variant="contained" disabled={loading} size="large" sx={{ width: '100%', borderRadius: '16px', py: 1.5, background: '#1e40af' }}>
            {loading ? <CircularProgress size={24} color="inherit" /> : "💾 حفظ البيانات"}
          </Button>
        </form>
      </Paper>

      {/* نافذة النجاح الموحدة */}
      <Dialog open={successModal} PaperProps={{ sx: { borderRadius: '24px', p: 3, width: '350px', textAlign: 'center' } }}>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
            <Box sx={{ width: 80, height: 80, borderRadius: '50%', border: '2px solid #22c55e', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#22c55e" strokeWidth="3"><polyline points="20 6 9 17 4 12"></polyline></svg>
            </Box>
            <Typography variant="h6" fontWeight="bold">تم الحفظ بنجاح</Typography>
          </Box>
        </DialogContent>
      </Dialog>
    </Container>
  );
}